import React from "react";
import { useNavigate } from "react-router-dom";
import { format } from "date-fns";
import { Button } from "../../ui/button";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import { Plus, Receipt, ChevronDown, UserPlus, FileText, MessageSquare, RefreshCw, CalendarDays } from "lucide-react";
import { DateRangeFilter } from "../../common/DateRangeFilter";
import { Overline } from "./singleUtils";

const CommandHeader = ({
  user, businessName, today, periodLabel, live, refreshing,
  dateFilter, onDateFilterChange, customStart, customEnd, onCustomChange,
  onAddExpense, financialAccess,
}) => {
  const navigate = useNavigate();
  const hour = today.getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  return (
    <div className="flex flex-col xl:flex-row xl:items-end xl:justify-between gap-3" data-testid="single-dash-header">
      <div className="min-w-0">
        <Overline className="text-amber-600 dark:text-amber-400">Command Center</Overline>
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-foreground font-['Manrope'] mt-0.5">
          {greeting}, {user?.name?.split(" ")[0]}
          {live && (
            <span className="inline-flex items-center gap-1 ml-2.5 align-middle text-[10px] font-semibold text-emerald-600 dark:text-emerald-400 bg-emerald-500/10 px-1.5 py-0.5 rounded-full" data-testid="single-dash-live">
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" /> Live
            </span>
          )}
          {refreshing && <RefreshCw className="inline w-4 h-4 ml-2 animate-spin text-muted-foreground/40" />}
        </h1>
        <p className="text-sm text-muted-foreground mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5" data-testid="single-dash-subheader">
          {businessName && <span className="font-semibold text-foreground/80">{businessName}</span>}
          <span className="inline-flex items-center gap-1"><CalendarDays className="w-3.5 h-3.5" />{periodLabel}, {format(today, "MMMM d, yyyy")}</span>
        </p>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <DateRangeFilter
          value={dateFilter}
          onChange={onDateFilterChange}
          customStart={customStart}
          customEnd={customEnd}
          onCustomChange={onCustomChange}
          triggerClassName="w-[165px] h-9 text-sm"
          dataTestId="header-date-filter"
        />
        <Button size="sm" className="h-9 bg-amber-500 hover:bg-amber-400 text-black font-semibold" onClick={() => navigate("/dashboard/jobs/new")} data-testid="quick-action-create-job">
          <Plus className="w-4 h-4 mr-1" /> Create Job
        </Button>
        {financialAccess && (
          <Button size="sm" variant="outline" className="h-9 border-amber-500/40 text-amber-600 dark:text-amber-400 hover:bg-amber-500/10" onClick={onAddExpense} data-testid="quick-action-add-expense">
            <Receipt className="w-4 h-4 mr-1" /> Add Expense
          </Button>
        )}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button size="sm" variant="outline" className="h-9 px-2" data-testid="quick-action-more"><ChevronDown className="w-4 h-4" /></Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem onClick={() => navigate("/dashboard/customers")} data-testid="quick-action-customer"><UserPlus className="w-3.5 h-3.5 mr-2" /> Create Customer</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/dashboard/invoices")} data-testid="quick-action-invoice"><FileText className="w-3.5 h-3.5 mr-2" /> Create Invoice</DropdownMenuItem>
            <DropdownMenuItem onClick={() => navigate("/dashboard/messages")} data-testid="quick-action-message"><MessageSquare className="w-3.5 h-3.5 mr-2" /> New Message</DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};

export default CommandHeader;
